import { useEffect, useRef } from 'react'

// ── Forest canvas: falling leaves + grass sway ────────────────────────────────
function ForestCanvas() {
  const ref = useRef()
  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas.getContext('2d')
    let raf, W, H

    const leaves = Array.from({length: 38}, () => ({
      x: Math.random() * 1400,
      y: Math.random() * -800,
      size: Math.random() * 12 + 6,
      speed: Math.random() * 1.2 + 0.4,
      drift: (Math.random() - 0.5) * 0.8,
      rot: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.04,
      opacity: Math.random() * 0.5 + 0.2,
      color: ['#4ade80','#86efac','#22c55e','#a3e635','#bbf7d0','#fbbf24'][Math.floor(Math.random()*6)],
      swing: Math.random() * Math.PI * 2,
      swingSpeed: Math.random() * 0.02 + 0.005,
    }))

    const grasses = Array.from({length: 60}, () => ({
      x: Math.random() * 1400,
      h: Math.random() * 60 + 20,
      width: Math.random() * 3 + 1,
      phase: Math.random() * Math.PI * 2,
      speed: Math.random() * 0.02 + 0.005,
      color: `rgba(${[74,200,100][Math.floor(Math.random()*3)]},${[222,240,180][Math.floor(Math.random()*3)]},${[128,150,80][Math.floor(Math.random()*3)]},${Math.random()*0.3+0.15})`,
    }))

    // Fireflies
    const flies = Array.from({length: 18}, () => ({
      x: Math.random() * 1400,
      y: Math.random() * 600 + 100,
      vx: (Math.random()-0.5)*0.6,
      vy: (Math.random()-0.5)*0.4,
      phase: Math.random()*Math.PI*2,
      size: Math.random()*2+1,
    }))

    let t = 0

    function resize() {
      W = canvas.width  = canvas.offsetWidth
      H = canvas.height = canvas.offsetHeight
    }
    resize()
    window.addEventListener('resize', resize)

    function drawLeaf(x, y, size, rot, color, opacity) {
      ctx.save()
      ctx.globalAlpha = opacity
      ctx.translate(x, y)
      ctx.rotate(rot)
      ctx.beginPath()
      ctx.moveTo(0, -size)
      ctx.bezierCurveTo(size*0.8, -size*0.5, size*0.8, size*0.5, 0, size)
      ctx.bezierCurveTo(-size*0.8, size*0.5, -size*0.8, -size*0.5, 0, -size)
      ctx.fillStyle = color
      ctx.fill()
      ctx.restore()
    }

    function draw() {
      ctx.clearRect(0, 0, W, H)
      t += 0.016

      // Draw grasses at bottom
      grasses.forEach(g => {
        const sway = Math.sin(t * g.speed * 60 + g.phase) * 12
        ctx.save()
        ctx.globalAlpha = 0.6
        ctx.strokeStyle = g.color
        ctx.lineWidth = g.width
        ctx.lineCap = 'round'
        ctx.beginPath()
        ctx.moveTo(g.x, H)
        ctx.quadraticCurveTo(g.x + sway * 0.5, H - g.h * 0.5, g.x + sway, H - g.h)
        ctx.stroke()
        ctx.restore()
      })

      // Draw leaves
      leaves.forEach(l => {
        l.swing += l.swingSpeed
        l.x += Math.sin(l.swing) * 0.8 + l.drift
        l.y += l.speed
        l.rot += l.rotSpeed
        if (l.y > H + 30) { l.y = -30; l.x = Math.random() * W }
        if (l.x > W + 30) l.x = -30
        if (l.x < -30) l.x = W + 30
        drawLeaf(l.x, l.y, l.size, l.rot, l.color, l.opacity)
      })

      // Fireflies
      flies.forEach(f => {
        f.x += f.vx; f.y += f.vy
        f.phase += 0.05
        if (f.x < 0 || f.x > W) f.vx *= -1
        if (f.y < 0 || f.y > H) f.vy *= -1
        const glow = (Math.sin(f.phase) + 1) / 2
        ctx.save()
        ctx.globalAlpha = glow * 0.7
        ctx.beginPath()
        ctx.arc(f.x, f.y, f.size, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(187,247,208,${glow})`
        ctx.fill()
        // glow ring
        ctx.globalAlpha = glow * 0.2
        ctx.beginPath()
        ctx.arc(f.x, f.y, f.size * 4, 0, Math.PI * 2)
        ctx.fillStyle = '#4ade80'
        ctx.fill()
        ctx.restore()
      })

      raf = requestAnimationFrame(draw)
    }
    draw()
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize) }
  }, [])

  return <canvas ref={ref} style={{position:'fixed',inset:0,width:'100%',height:'100%',pointerEvents:'none',zIndex:0,opacity:0.6}} />
}

// ── Ocean canvas: waves + bubbles + light rays ────────────────────────────────
function OceanCanvas() {
  const ref = useRef()
  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas.getContext('2d')
    let raf, W, H, t = 0

    const bubbles = Array.from({length: 35}, () => ({
      x: Math.random() * 1400,
      y: Math.random() * 800 + 200,
      r: Math.random() * 5 + 1.5,
      speed: Math.random() * 0.8 + 0.2,
      drift: (Math.random()-0.5) * 0.4,
      opacity: Math.random() * 0.4 + 0.1,
    }))

    function resize() { W = canvas.width = canvas.offsetWidth; H = canvas.height = canvas.offsetHeight }
    resize()
    window.addEventListener('resize', resize)

    function draw() {
      ctx.clearRect(0, 0, W, H)
      t += 0.008

      // Light rays from top
      for (let i = 0; i < 6; i++) {
        const x = W * (0.1 + i * 0.16) + Math.sin(t + i) * 30
        const grad = ctx.createLinearGradient(x, 0, x + 80, H * 0.7)
        grad.addColorStop(0, 'rgba(56,189,248,0.08)')
        grad.addColorStop(1, 'rgba(56,189,248,0)')
        ctx.save()
        ctx.globalAlpha = 0.5 + Math.sin(t*0.5 + i) * 0.2
        ctx.beginPath()
        ctx.moveTo(x - 20, 0)
        ctx.lineTo(x + 20, 0)
        ctx.lineTo(x + 100, H * 0.7)
        ctx.lineTo(x + 60, H * 0.7)
        ctx.closePath()
        ctx.fillStyle = grad
        ctx.fill()
        ctx.restore()
      }

      // Waves
      for (let w = 0; w < 4; w++) {
        ctx.save()
        ctx.globalAlpha = 0.06 - w * 0.01
        ctx.beginPath()
        ctx.moveTo(0, H * 0.3 + w * 60)
        for (let x = 0; x <= W; x += 8) {
          const y = H * 0.3 + w * 60 + Math.sin((x / W) * Math.PI * 4 + t * (1 + w * 0.3)) * (20 - w * 3)
          ctx.lineTo(x, y)
        }
        ctx.lineTo(W, H); ctx.lineTo(0, H); ctx.closePath()
        ctx.fillStyle = `rgba(56,189,248,1)`
        ctx.fill()
        ctx.restore()
      }

      // Bubbles
      bubbles.forEach(b => {
        b.y -= b.speed; b.x += b.drift
        if (b.y < -10) { b.y = H + 10; b.x = Math.random() * W }
        ctx.save()
        ctx.globalAlpha = b.opacity
        ctx.beginPath()
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2)
        ctx.strokeStyle = 'rgba(186,230,253,0.7)'
        ctx.lineWidth = 0.8
        ctx.stroke()
        ctx.restore()
      })

      raf = requestAnimationFrame(draw)
    }
    draw()
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize) }
  }, [])

  return <canvas ref={ref} style={{position:'fixed',inset:0,width:'100%',height:'100%',pointerEvents:'none',zIndex:0,opacity:0.8}} />
}

// ── Blizzard canvas: snow + wind streaks + aurora ─────────────────────────────
function BlizzardCanvas() {
    const ref = useRef()
    useEffect(() => {
      const canvas = ref.current
      const ctx = canvas.getContext('2d')
      let raf, W, H, t = 0
  
      const flakes = Array.from({ length: 120 }, () => ({
        x: Math.random() * 1400,
        y: Math.random() * 900,
        r: Math.random() * 3.5 + 0.8,
        speed: Math.random() * 2.5 + 0.8,
        drift: (Math.random() - 0.5) * 1.2,
        phase: Math.random() * Math.PI * 2,
        phaseSpeed: Math.random() * 0.03 + 0.01,
        opacity: Math.random() * 0.6 + 0.3,
      }))
  
      // Wind streaks
      const streaks = Array.from({ length: 25 }, () => ({
        x: Math.random() * 1400,
        y: Math.random() * 900,
        len: Math.random() * 80 + 30,
        speed: Math.random() * 6 + 3,
        opacity: Math.random() * 0.08 + 0.02,
        vy: (Math.random() - 0.5) * 0.5,
      }))
  
      // Aurora bands
      const auroras = [
        { y: 0.18, color: [0, 255, 150],   speed: 0.18, amp: 28, phase: 0 },
        { y: 0.28, color: [80, 150, 255],  speed: 0.13, amp: 20, phase: 2 },
        { y: 0.38, color: [180, 80, 255],  speed: 0.22, amp: 16, phase: 4 },
      ]
  
      function resize() {
        W = canvas.width = canvas.offsetWidth
        H = canvas.height = canvas.offsetHeight
      }
      resize()
      window.addEventListener('resize', resize)
  
      function draw() {
        ctx.clearRect(0, 0, W, H)
        t += 0.016
  
        // Aurora bands
        auroras.forEach(a => {
          const baseY = H * a.y
          ctx.save()
          ctx.beginPath()
          ctx.moveTo(0, baseY)
          for (let x = 0; x <= W; x += 6) {
            const y = baseY
              + Math.sin((x / W) * Math.PI * 3 + t * a.speed + a.phase) * a.amp
              + Math.sin((x / W) * Math.PI * 6 - t * a.speed * 0.7 + a.phase) * (a.amp * 0.4)
            ctx.lineTo(x, y)
          }
          const [r, g, b] = a.color
          const pulse = 0.12 + Math.sin(t * a.speed * 2 + a.phase) * 0.04
          const grad = ctx.createLinearGradient(0, baseY - a.amp * 2, 0, baseY + a.amp * 2)
          grad.addColorStop(0, `rgba(${r},${g},${b},0)`)
          grad.addColorStop(0.5, `rgba(${r},${g},${b},${pulse})`)
          grad.addColorStop(1, `rgba(${r},${g},${b},0)`)
          ctx.strokeStyle = `rgba(${r},${g},${b},${pulse * 1.5})`
          ctx.lineWidth = 1.5
          ctx.stroke()
          // fill band
          ctx.lineTo(W, baseY + a.amp * 3)
          ctx.lineTo(0, baseY + a.amp * 3)
          ctx.closePath()
          ctx.fillStyle = grad
          ctx.fill()
          ctx.restore()
        })
  
        // Wind streaks
        streaks.forEach(s => {
          s.x -= s.speed
          s.y += s.vy
          if (s.x < -s.len) { s.x = W + 20; s.y = Math.random() * H }
          ctx.save()
          ctx.globalAlpha = s.opacity
          ctx.strokeStyle = 'rgba(200,230,255,0.9)'
          ctx.lineWidth = 0.6
          ctx.beginPath()
          ctx.moveTo(s.x, s.y)
          ctx.lineTo(s.x + s.len, s.y + s.vy * 4)
          ctx.stroke()
          ctx.restore()
        })
  
        // Snowflakes
        flakes.forEach(f => {
          f.phase += f.phaseSpeed
          f.x += Math.sin(f.phase) * 0.6 + f.drift - 1.2 // wind pushes left
          f.y += f.speed
          if (f.y > H + 10) { f.y = -10; f.x = Math.random() * W }
          if (f.x < -10) f.x = W + 10
          if (f.x > W + 10) f.x = -10
  
          const glow = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, f.r * 2.5)
          glow.addColorStop(0, `rgba(220,240,255,${f.opacity})`)
          glow.addColorStop(0.5, `rgba(190,215,255,${f.opacity * 0.5})`)
          glow.addColorStop(1, 'rgba(150,200,255,0)')
          ctx.beginPath()
          ctx.arc(f.x, f.y, f.r * 2.5, 0, Math.PI * 2)
          ctx.fillStyle = glow
          ctx.fill()
  
          // solid core
          ctx.beginPath()
          ctx.arc(f.x, f.y, f.r * 0.6, 0, Math.PI * 2)
          ctx.fillStyle = `rgba(240,248,255,${f.opacity * 0.9})`
          ctx.fill()
        })
  
        // Ground snow drift glow
        const snowGlow = ctx.createLinearGradient(0, H * 0.88, 0, H)
        snowGlow.addColorStop(0, 'rgba(180,210,255,0)')
        snowGlow.addColorStop(1, 'rgba(200,225,255,0.12)')
        ctx.fillStyle = snowGlow
        ctx.fillRect(0, H * 0.88, W, H * 0.12)
  
        raf = requestAnimationFrame(draw)
      }
      draw()
      return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize) }
    }, [])
  
    return (
      <canvas
        ref={ref}
        style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0, opacity: 0.85 }}
      />
    )
  }
  
// ── Fire canvas: flames + embers ──────────────────────────────────────────────
function FireCanvas() {
  const ref = useRef()
  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas.getContext('2d')
    let raf, W, H, t = 0

    const embers = Array.from({length: 60}, () => ({
      x: Math.random() * 1400,
      y: Math.random() * 800,
      vx: (Math.random()-0.5) * 1.5,
      vy: -(Math.random() * 2 + 0.5),
      size: Math.random() * 3 + 1,
      life: Math.random(),
      decay: Math.random() * 0.005 + 0.002,
      color: ['#fb923c','#fbbf24','#fca5a5','#f97316','#fde68a'][Math.floor(Math.random()*5)],
    }))

    function resize() { W = canvas.width = canvas.offsetWidth; H = canvas.height = canvas.offsetHeight }
    resize()
    window.addEventListener('resize', resize)

    function drawFlame(x, baseY, w, h, phase) {
      ctx.save()
      ctx.beginPath()
      ctx.moveTo(x - w/2, baseY)
      ctx.bezierCurveTo(
        x - w/2 + Math.sin(phase) * 10, baseY - h * 0.3,
        x - w/4 + Math.sin(phase * 1.3) * 8, baseY - h * 0.7,
        x + Math.sin(phase * 0.7) * 5, baseY - h
      )
      ctx.bezierCurveTo(
        x + w/4 + Math.sin(phase * 1.1) * 8, baseY - h * 0.7,
        x + w/2 + Math.sin(phase * 0.9) * 10, baseY - h * 0.3,
        x + w/2, baseY
      )
      ctx.closePath()
      const grad = ctx.createLinearGradient(x, baseY, x, baseY - h)
      grad.addColorStop(0, 'rgba(251,146,60,0.25)')
      grad.addColorStop(0.5, 'rgba(251,191,36,0.12)')
      grad.addColorStop(1, 'rgba(248,113,113,0)')
      ctx.fillStyle = grad
      ctx.fill()
      ctx.restore()
    }

    function draw() {
      ctx.clearRect(0, 0, W, H)
      t += 0.02

      // Background flames
      for (let i = 0; i < 8; i++) {
        drawFlame(W * (0.05 + i * 0.13), H, 80 + i * 15, 200 + Math.sin(t + i) * 80, t * 1.2 + i)
      }

      // Embers
      embers.forEach(e => {
        e.x += e.vx + Math.sin(t * 2 + e.x) * 0.3
        e.y += e.vy
        e.life -= e.decay
        if (e.life <= 0) {
          e.x = Math.random() * W
          e.y = H + 10
          e.life = 1
          e.vy = -(Math.random() * 2.5 + 0.5)
        }
        ctx.save()
        ctx.globalAlpha = e.life * 0.8
        ctx.beginPath()
        ctx.arc(e.x, e.y, e.size * e.life, 0, Math.PI * 2)
        ctx.fillStyle = e.color
        ctx.fill()
        ctx.restore()
      })

      raf = requestAnimationFrame(draw)
    }
    draw()
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize) }
  }, [])
  return <canvas ref={ref} style={{position:'fixed',inset:0,width:'100%',height:'100%',pointerEvents:'none',zIndex:0,opacity:0.9}} />
}

// ── Neon canvas: grid + particles ────────────────────────────────────────────
function NeonCanvas() {
  const ref = useRef()
  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas.getContext('2d')
    let raf, W, H, t = 0

    const particles = Array.from({length: 40}, () => ({
      x: Math.random() * 1400, y: Math.random() * 900,
      vx: (Math.random()-0.5) * 0.5, vy: (Math.random()-0.5) * 0.5,
      size: Math.random() * 2 + 0.5,
      color: ['#7c3aed','#06b6d4','#f472b6'][Math.floor(Math.random()*3)],
      phase: Math.random() * Math.PI * 2,
    }))

    function resize() { W = canvas.width = canvas.offsetWidth; H = canvas.height = canvas.offsetHeight }
    resize()
    window.addEventListener('resize', resize)

    function draw() {
      ctx.clearRect(0, 0, W, H)
      t += 0.008

      // Grid
      const gridSize = 60
      ctx.save()
      ctx.strokeStyle = 'rgba(124,58,237,0.07)'
      ctx.lineWidth = 0.5
      for (let x = 0; x < W; x += gridSize) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke()
      }
      for (let y = 0; y < H; y += gridSize) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke()
      }
      ctx.restore()

      // Particles
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.phase += 0.03
        if (p.x < 0 || p.x > W) p.vx *= -1
        if (p.y < 0 || p.y > H) p.vy *= -1
        const glow = (Math.sin(p.phase) + 1) / 2
        ctx.save()
        ctx.globalAlpha = glow * 0.6 + 0.2
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
        ctx.fillStyle = p.color
        ctx.fill()
        ctx.restore()
      })

      // Connect nearby particles
      particles.forEach((p, i) => {
        particles.slice(i+1).forEach(q => {
          const dx = p.x - q.x, dy = p.y - q.y
          const dist = Math.sqrt(dx*dx + dy*dy)
          if (dist < 120) {
            ctx.save()
            ctx.globalAlpha = (1 - dist/120) * 0.15
            ctx.strokeStyle = p.color
            ctx.lineWidth = 0.5
            ctx.beginPath()
            ctx.moveTo(p.x, p.y)
            ctx.lineTo(q.x, q.y)
            ctx.stroke()
            ctx.restore()
          }
        })
      })

      raf = requestAnimationFrame(draw)
    }
    draw()
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize) }
  }, [])
  return <canvas ref={ref} style={{position:'fixed',inset:0,width:'100%',height:'100%',pointerEvents:'none',zIndex:0,opacity:1}} />
}

// ── Classic: subtle floating dots ────────────────────────────────────────────
function ClassicCanvas() {
  const ref = useRef()
  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas.getContext('2d')
    let raf, W, H, t = 0

    const dots = Array.from({length: 20}, () => ({
      x: Math.random() * 1400, y: Math.random() * 900,
      vx: (Math.random()-0.5) * 0.2, vy: (Math.random()-0.5) * 0.2,
      r: Math.random() * 80 + 30, phase: Math.random() * Math.PI * 2,
    }))

    function resize() { W = canvas.width = canvas.offsetWidth; H = canvas.height = canvas.offsetHeight }
    resize()
    window.addEventListener('resize', resize)

    function draw() {
      ctx.clearRect(0, 0, W, H)
      t += 0.005
      dots.forEach(d => {
        d.x += d.vx; d.y += d.vy; d.phase += 0.01
        if (d.x < -100 || d.x > W+100) d.vx *= -1
        if (d.y < -100 || d.y > H+100) d.vy *= -1
        const alpha = (Math.sin(d.phase) + 1) * 0.015
        ctx.beginPath()
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(37,99,235,${alpha})`
        ctx.fill()
      })
      raf = requestAnimationFrame(draw)
    }
    draw()
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize) }
  }, [])
  return <canvas ref={ref} style={{position:'fixed',inset:0,width:'100%',height:'100%',pointerEvents:'none',zIndex:0}} />
}

export default function ThemeCanvas({ themeKey }) {
  if (themeKey === 'forest')  return <ForestCanvas />
  if (themeKey === 'ocean')   return <OceanCanvas />
  if (themeKey === 'fire')    return <FireCanvas />
  if (themeKey === 'neon')    return <NeonCanvas />
  if (themeKey === 'classic') return <ClassicCanvas />
  return null
}